/* eslint-disable no-throw-literal */
import { useLingui } from "@lingui/react";
import { useMemo } from "react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { evolve, path, replace } from "ramda";
import { AJR, IJR } from "../../../settings";
import { flow, identity, pipe } from "fp-ts/lib/function";
import { either } from "fp-ts";
import { useRubriek } from "components/aangifte/useRubriek";
import { useAangifte } from "components/aangifte/AangifteProvider";
import { rubriekFp } from "domain/rubriek";
import { Rubriek } from "domain/store/rubriekenReducer";

const codesVoorafBetalingen = ["1570", "1571", "1572", "1573", "1574", "2570", "2571", "2572", "2573", "2574"];
const codesBurgerlijkeStaat = [
  "1001",
  "1002",
  "1003",
  "1004",
  "1010",
  "1011",
  "1018",
  "1019",
  "1020",
  "1021",
  "1022",
  "1023",
  "1024",
];
const codesGezinslasten = [
  // persoonlijke gegevens
  // eerste vestiging zelfstandige
  "1552",
  "2552",
  // handicap
  "1028",
  "2028",
  // gezinslasten
  "1030",
  "1031",
  "1038",
  "1039",
  "1034",
  "1035",
  "1054",
  "1055",
  "1036",
  "1037",
  "1058",
  "1059",
  "1027",
  "1029",
  "1043",
  "1044",
  "1032",
  "1033",
];

export const noQuickEntryCodes = [
  ...codesVoorafBetalingen,
  ...codesBurgerlijkeStaat,
  ...codesGezinslasten,
];

const extractGewestNameForLocale = (locale: string) => evolve({ name: path([locale]) });

const replaceJaartallen = evolve({
  name: flow(
    replace("{AJR}", AJR.toString()),
    replace("{IJR}", IJR.toString()),
    replace("{IJR-1}", (IJR - 1).toString()),
    replace("{IJR-2}", (IJR - 2).toString()),
    replace("{IJR-3}", (IJR - 3).toString()),
    replace("{IJR-8}", (IJR - 8).toString()),
    replace("{IJR-9}", (IJR - 9).toString()),
    replace("{IJR-12}", (IJR - 12).toString())
  ),
});

interface ValidatePartnerCodeProps {
  code: string;
  isGemeenschappelijkeAangifte: boolean;
}

const validatePartnerCode = ({ code, isGemeenschappelijkeAangifte }: ValidatePartnerCodeProps) => (
  rubriek: Rubriek
) => {
  if (rubriekFp.isPartnerRubriek(code, rubriek) && !isGemeenschappelijkeAangifte)
    return either.left({ error: i18n._(t`Deze code is niet toegelaten voor alleenstaanden`) as string });

  return either.right(rubriek);
};

const validateRubriekCode = (code: string) => (rubriek: Rubriek) => {
  if (noQuickEntryCodes.includes(code) || rubriekFp.isInternalRubriek(rubriek)) return either.left({ error: i18n._(t`Ongeldige code`) as string });

  return either.right(rubriek);
};

export type QuickEntryRubriek = Omit<Rubriek, "name"> & { name: string };

export const useQuickEntryRubriek = (code: string): { rubriek?: QuickEntryRubriek; error?: string } => {
  const rubriek = useRubriek(code);
  const {
    aangifte: { isGemeenschappelijkeAangifte },
  } = useAangifte();
  const {
    i18n: { locale },
  } = useLingui();

  return useMemo(() => {
    return pipe(
      rubriek,
      either.fromOption(() => ({ error: i18n._(t`Ongeldige code`) as string })),
      either.chain(validateRubriekCode(code)),
      either.chain(validatePartnerCode({ code, isGemeenschappelijkeAangifte })),
      either.map(extractGewestNameForLocale(locale)),
      either.map(replaceJaartallen),
      either.foldW(identity, (rubriek) => ({ rubriek }))
    );
  }, [code, isGemeenschappelijkeAangifte, locale, rubriek]);
};
